import type {
  AdminAccountKind,
  AgencyStage,
  AnchorageGeofence,
  AuditEventType,
  CargoCategory,
  DispatchMode,
  ErrorBody,
  ErrorCode,
  GeoPoint,
  OrderStatus,
  PricingMatrix,
  PricingTableStatus,
  ProofNode,
  QuoteBreakdown,
  RefundStatus,
  ReviewAuthorRole,
  ServiceType,
  SettlementStatus,
  SignatureVia,
  TransportMode,
} from '@sea-drone/shared'

/**
 * 运营端 HTTP 客户端。
 *
 * 只做一件重要的事:**非 2xx 一律抛 ApiError,绝不返回 null 或空数组。**
 * 返回空数组的客户端会让「请求失败」和「一条数据都没有」在页面上长得一模一样,
 * 而那正是运营在验收时最容易被骗过去的地方 —— 页面白着,没人知道是空还是坏。
 */

export class ApiError extends Error {
  readonly code: ErrorCode | 'HTTP_TRANSPORT'
  readonly status: number
  readonly requestId: string
  readonly details?: Record<string, unknown>

  constructor(opts: {
    code: ErrorCode | 'HTTP_TRANSPORT'
    message: string
    status: number
    requestId: string
    details?: Record<string, unknown>
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.code = opts.code
    this.status = opts.status
    this.requestId = opts.requestId
    this.details = opts.details
  }
}

const TOKEN_KEY = 'sea-drone.ops.token'

/**
 * 运营端令牌 —— S-10 起是**账号登录换来的**(POST /api/admin/auth/login),
 * 存在 localStorage 里,12 小时过期。
 *
 * ═══ 那个 'dev-ops-token' 的兜底没了,而且不许加回来 ═══
 *
 * S-01 起这个函数在没有令牌时返回一个硬编码的 'dev-ops-token' —— 那是一个
 * 写进仓库的凭证,而且它让「没登录」表现成「登录了但令牌不对」。现在没有令牌就是
 * **空字符串**,而 401 处理会把人送到登录页(见下面的 request)。
 */
export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ''
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export function hasToken(): boolean {
  return getToken() !== ''
}

/**
 * 令牌过期或被停用时把人送回登录页。
 *
 * 为什么在这里做而不是每个页面各判一次:漏一处的表现是「页面一直转圈 / 一直显示
 * 上一次的错误」,而运营会以为是服务坏了。跳登录页这个动作由**唯一的请求出口**做,
 * 才不会漏。
 *
 * 注意它**不吞掉错误**:ApiError 照样抛出去,调用方照样能显示那句话。
 */
function redirectToLogin(): void {
  clearToken()
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login')
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${getToken()}`,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (e) {
    // 网络层失败也要变成一个带得出去的错误对象,不许在这里咽掉
    throw new ApiError({
      code: 'HTTP_TRANSPORT',
      message: `连不上 api 服务(${method} ${path}):${e instanceof Error ? e.message : String(e)}。先确认 pnpm dev:api 起着`,
      status: 0,
      requestId: 'no-request-id',
    })
  }

  const text = await res.text()

  if (!res.ok) {
    const parsed = parseErrorBody(text)
    if (res.status === 401) redirectToLogin()
    throw new ApiError({
      code: parsed?.code ?? 'HTTP_TRANSPORT',
      message: parsed?.message ?? `${method} ${path} 返回 ${res.status},响应体不是契约里的 Error 结构:${text.slice(0, 200)}`,
      status: res.status,
      requestId: parsed?.requestId ?? res.headers.get('x-request-id') ?? 'no-request-id',
      ...(parsed?.details ? { details: parsed.details } : {}),
    })
  }

  if (text.length === 0) return undefined as T
  return JSON.parse(text) as T
}

function parseErrorBody(text: string): ErrorBody | null {
  let json: unknown
  try {
    json = JSON.parse(text)
  } catch {
    /* @intentional-swallow(reason="这里的失败本身就是返回值:响应体不是 JSON。调用方拿到 null 之后会把原始文本连同状态码一起抛出去,信息不会丢") */
    return null
  }
  if (json && typeof json === 'object' && typeof (json as ErrorBody).code === 'string') {
    return json as ErrorBody
  }
  return null
}

// ── 契约里 S-01 那 8 条 ──────────────────────────────────

export interface CarrierApplication {
  applicationId: string
  companyName: string
  status: 'pending' | 'approved' | 'rejected'
  reviewNote: string | null
  submittedAt: string
}

/** 契约 components.schemas.CarrierApplicationAttachment —— **没有对象键**,只有类型与短时效签名 URL */
export interface CarrierApplicationAttachment {
  /** 营业执照 / 运输资质(材料带类型)。中文名用 @sea-drone/ui 的 carrierMaterialTypeLabel 渲染 */
  type: 'business_license' | 'transport_permit'
  url: string
  urlExpiresAt: string
}

/** 契约 components.schemas.CarrierApplicationDetail —— 审核人点开一条时看到的全部材料(M-27) */
export interface CarrierApplicationDetail {
  applicationId: string
  companyName: string
  status: 'pending' | 'approved' | 'rejected'
  reviewNote: string | null
  submittedAt: string
  reviewedAt: string | null
  carrierId: string | null
  uniformCreditCode: string
  contactName: string
  contactPhone: string
  attachments: CarrierApplicationAttachment[]
}

export interface Carrier {
  carrierId: string
  companyName: string
  commissionRateBp: number
  selfOperated: boolean
  enabled: boolean
}

export interface Employee {
  name: string
  phoneMasked: string
  idNumberMasked: string
  /** 拉黑标记(M-05):人还在名单里,只是不再解析出飞手角色 */
  blacklisted: boolean
}

export interface Page<T> {
  items: T[]
  page: number
  pageSize: number
  total: number
}

// ── 契约里 S-02 那 3 条(第四条 /api/quotes 见下面那处说明)──
//
// 类型不在这里重写一遍:计价表 payload 与计价明细的形状定义在
// @sea-drone/shared(packages/shared/src/pricing.ts),运营端直接引用。
// 前端抄一份 interface 的下场是某天后端加了一个档位、前端的类型里没有,
// 而那一项在页面上就静默不显示了。

export interface PricingTable {
  version: string
  status: PricingTableStatus
  publishedAt: string | null
  createdAt: string
}

export interface PricingTableInput {
  version: string
  anchorageInside: PricingMatrix
  anchorageOutside: PricingMatrix
  anchorageGeofence: AnchorageGeofence
}

/**
 * 订单计价快照的形状(M-11)。它同时是契约里 /api/quotes 的返回体,
 * 但**运营端不调那条接口** —— 见 api 对象里 S-02 那一段末尾的说明。
 */
export interface Quote extends QuoteBreakdown {
  pricingTableVersion: string
}

// ── 契约里 S-10 那几条 ─────────────────────────────────
//
// 订单详情与平台配置的形状定义在 @sea-drone/shared 与契约里,这里只写这个前端
// 真正要用到的子集。**不在前端重抄一遍完整类型**:抄一份的下场是后端加了字段、
// 前端的类型里没有,而那一项在页面上静默不显示(同 S-02 那段注释的理由)。

/** 契约 components.schemas.AdminIdentity(S-10 新增) */
export interface AdminIdentity {
  userId: string
  username: string
  kind: AdminAccountKind
}

export interface AdminLoginResult {
  token: string
  expiresAt: string
  identity: AdminIdentity
}

/** 契约 components.schemas.PlatformConfig */
export interface PlatformConfig {
  priorityWindowEnabled: boolean
  priorityWindowMinutes: number
  deadRunFee: number
  /** M-13(2026-10-11 修订):代办费是后台配置的一个**固定数额**,没有费率形态 */
  agencyFeeDefault: number
}

/**
 * 契约 components.schemas.GeoPoint 在运营端的样子。
 *
 * shared 的 GeoPoint 只有 lat / lng(它是给大圆距离算的),而契约那一份还有两个可空字段,
 * 订单驾驶舱的「路线」列取的正是 `area`(订单池对承运商只给到区域粒度,M-08)——
 * 按 shared 那份写会读不出 area,按契约抄一份完整的是这里唯一说得通的做法。
 */
export interface AdminGeoPoint extends GeoPoint {
  address: string | null
  area: string | null
}

/**
 * 契约 components.schemas.OrderDetail 里运营端驾驶舱会用到的那些字段。
 *
 * 列全不是「抄一遍契约」—— 少一个字段的下场是**它在页面上静默不显示**
 * (类型里没有、模板里就写不出来、也就没人发现契约里有这一项)。
 * S-42 补上的六个正是这样漏掉的一批:
 *   · pilotName          —— L0-01,这一单是谁飞的(放池单抢单成交才有值)
 *   · origin/destination —— GeoPoint,列表的「路线」那一列取它的 area
 *   · refundStatus       —— M-21,退款单状态(没退过款为 null)
 *   · refundAmount       —— M-12,退给客户的那一笔
 *   · deadRunFee         —— AS-054 / Y-27,客户可读的空跑费
 * 它们四个(除起终点)同时是 O1 抽屉里「这一轮补上的四个字段」那张卡的全部内容。
 */
/**
 * 一条评价(契约 components.schemas.Review,S-57 / S-58)。运营端订单详情能看到这一单的两条
 * (下单人评飞手 + 飞手评下单人,Y-52)。方向由 authorRole 说清 —— 收件人不参与评价(Y-51),
 * 所以这里不会出现「收件人」这个取值。中文名走 @sea-drone/shared 的 reviewAuthorRoleLabel。
 */
export interface OrderReview {
  rating: number
  content: string | null
  authorRole: ReviewAuthorRole
  createdAt: string
}

export interface AdminOrderDetail {
  orderNo: string
  status: OrderStatus
  serviceType: ServiceType
  dispatchMode: DispatchMode
  carrierId: string | null
  carrierName: string | null
  pilotName: string | null
  cargoCategory: CargoCategory
  origin: AdminGeoPoint
  destination: AdminGeoPoint
  contact: { name: string; phone: string }
  priceSnapshot: Quote
  commissionRateBpSnapshot: number | null
  paidAmount: number | null
  /** 退款单状态。没退过款为 null —— 中文名走 @sea-drone/ui 的 refundStatusLabel */
  refundStatus: RefundStatus | null
  /** 退给客户的那一笔(整数分)。没退过款为 null */
  refundAmount: number | null
  /** 空跑费(整数分,配置的当前值)。没接单过的单是 0,不是 null */
  deadRunFee: number
  /** 签收时间(S-55,Y-44)。从没签过为 null —— 中文名走 @sea-drone/ui 的 signatureViaLabel */
  signedAt: string | null
  /** 签收方式(S-55)。user 收件人自己点 / auto 到点自动 / ops 运营代确认 */
  signedVia: SignatureVia | null
  /** 签收的账号(auto / ops 为空) */
  signedByUserId: string | null
  /**
   * 这一单的评价(S-57 / Y-52)。运营端订单详情返回**全部**两条(下单人评飞手 + 飞手评下单人);
   * 客户侧与飞手侧的详情**没有**这个字段(当事人走 GET /api/orders/{orderNo}/reviews,带盲评过滤)。
   * 没评过时是空数组。
   */
  reviews: OrderReview[]
  legs: { legNo: number; mode: TransportMode; status: OrderStatus }[]
  agencyStage: AgencyStage | null
  createdAt: string
}

export interface AuditLogItem {
  eventType: AuditEventType
  orderNo: string | null
  actorId: string | null
  detail: Record<string, unknown>
  occurredAt: string
}

/**
 * 契约 components.schemas.Proof(S-47 起运营端也读它,M-04 / Y-02 / Y-35)。
 *
 * **短时效签名 URL,不是公开直链**:每次请求现签、urlExpiresAt 是它的失效时刻
 * (Y-02:照片私有读)。takenAt 可空(飞手手机上的拍摄时刻是选填,不参与任何判断);
 * uploadedAt 恒有,是可信的登记时刻。
 *
 * **未留证的节点不出现在 items 里** —— 不是给一行空值。页面按共享包的 PROOF_NODE
 * 补出「待留证」那几行;把「没有这一行」读成「这个节点不存在」的话,
 * 四个节点在页面上会少画几个,而那一屏看起来完全正常。
 */
export interface Proof {
  /** 节点名走共享包 @sea-drone/shared 的 ProofNode,中文名走 proofNodeLabel(单点) */
  node: ProofNode
  fileKey: string
  url: string
  urlExpiresAt: string
  uploadedAt: string
  takenAt: string | null
  /** 这个节点此前被顶掉过几张(边界矩阵 B4:重传保留历史,不静默丢弃) */
  supersededCount: number
}

/**
 * 契约 components.schemas.Settlement(S-43 加)。**五个必填金额 + 状态 + 交易号**,
 * 金额一律整数分(M-14)。
 *
 * 三个可空字段的「没有」与「是 0」是两件事,所以它们是可空的而不是 0:
 *   · deadRunFee / refundAmount —— 没退过款的单没有这两个数(S-08 的三段退款才会写)
 *   · transactionId —— 微信交易号,与商户单号一起是支付回调的幂等键(M-15)
 *
 * **这里没有承运商、也没有任何时间字段** —— 契约里没有,不是漏抄(S-43 的结算页
 * 那两列因此画破折号,见 views/SettlementsView.vue 里那两条 @unimplemented)。
 */
export interface Settlement {
  orderNo: string
  paidAmount: number
  freightFee: number
  agencyFee: number
  commissionBase: number
  commissionRateBp: number
  commissionAmount: number
  carrierPayable: number
  deadRunFee: number | null
  refundAmount: number | null
  status: SettlementStatus
  transactionId: string | null
}

// ── 契约里 S-11 增补的三条(监管端账号的开户与停用,M-07)──

/** 契约 components.schemas.GovAccount。**没有 passwordHash** —— 服务端就不给 */
export interface GovAccount {
  accountId: string
  username: string
  kind: AdminAccountKind
  enabled: boolean
  createdAt: string
}

export const api = {
  /**
   * 运营端登录(S-10)。**这是唯一一个不带令牌的请求** ——
   * 还没有令牌的人才需要调它。失败一律 401,而且服务端不区分
   * 「用户名不存在」与「密码不对」(那两句话不一样就等于一台账号枚举器)。
   */
  login: (username: string, password: string) =>
    request<AdminLoginResult>('POST', '/api/admin/auth/login', { username, password }),

  submitApplication: (input: {
    companyName: string
    uniformCreditCode: string
    contactName: string
    contactPhone: string
  }) => request<CarrierApplication>('POST', '/api/carrier-applications', input),

  listApplications: () =>
    request<Page<CarrierApplication>>('GET', '/api/admin/carrier-applications?page=1&pageSize=100'),

  /**
   * 一条申请的完整材料(M-27)。**列表里刻意没有它们** —— 一屏 100 条,
   * 每条都带上明文手机号与材料签名 URL 等于每刷一次列表就发 100 个能打开材料的链接。
   * 审核人点开哪一条就拉哪一条。
   */
  getApplicationDetail: (applicationId: string) =>
    request<CarrierApplicationDetail>('GET', `/api/admin/carrier-applications/${encodeURIComponent(applicationId)}`),

  review: (applicationId: string, input: { approve: boolean; commissionRateBp?: number; note?: string }) =>
    request<CarrierApplication>('POST', `/api/admin/carrier-applications/${applicationId}/review`, input),

  listCarriers: () => request<Page<Carrier>>('GET', '/api/admin/carriers?page=1&pageSize=100'),

  createCarrier: (input: {
    companyName: string
    uniformCreditCode?: string
    commissionRateBp: number
    selfOperated?: boolean
  }) => request<Carrier>('POST', '/api/admin/carriers', input),

  patchCarrier: (
    carrierId: string,
    patch: { commissionRateBp?: number; selfOperated?: boolean; enabled?: boolean },
  ) => request<Carrier>('PATCH', `/api/admin/carriers/${carrierId}`, patch),

  listEmployees: (carrierId: string) =>
    request<Page<Employee>>('GET', `/api/admin/carriers/${carrierId}/employees?page=1&pageSize=100`),

  replaceEmployees: (
    carrierId: string,
    employees: { name: string; idNumber: string; phone: string; blacklisted?: boolean }[],
  ) => request<Page<Employee>>('PUT', `/api/admin/carriers/${carrierId}/employees`, { employees }),

  // S-02
  listPricingTables: () =>
    request<Page<PricingTable>>('GET', '/api/admin/pricing-tables?page=1&pageSize=100'),

  createPricingTable: (input: PricingTableInput) =>
    request<PricingTable>('POST', '/api/admin/pricing-tables', input),

  publishPricingTable: (version: string) =>
    request<PricingTable>('POST', `/api/admin/pricing-tables/${encodeURIComponent(version)}/publish`),

  /**
   * S-02 的第四条 POST /api/quotes **没有方法,而且不许加回来**(N-07,2026-09-22)。
   *
   * 那条接口的受众是小程序端:匿名试算能被拿去批量反推整张价目表,所以它只收
   * 小程序令牌。运营端拿着 ops 令牌去调必然 401,而这里的统一 401 处理会清掉令牌
   * 把人送回登录页 —— 加了它,运营点一下的体验是「我被退出登录了」。
   * 要核对价格,走订单详情里成交当时的快照(M-11)。真的需要运营端试算时,
   * 先落一条决策、在契约里加一条运营端受众的接口,再在这个对象里加方法。
   */

  // ── S-10:全部订单 / 代办推进 / 人工指派 / 平台配置 / 审计日志 ──

  listOrders: (q: { status?: OrderStatus; serviceType?: ServiceType; page?: number; pageSize?: number } = {}) => {
    const params = new URLSearchParams({ page: String(q.page ?? 1), pageSize: String(q.pageSize ?? 50) })
    if (q.status) params.set('status', q.status)
    if (q.serviceType) params.set('serviceType', q.serviceType)
    return request<Page<AdminOrderDetail>>('GET', `/api/admin/orders?${params.toString()}`)
  },

  getOrder: (orderNo: string) =>
    request<AdminOrderDetail>('GET', `/api/admin/orders/${encodeURIComponent(orderNo)}`),

  /**
   * 四节点凭证照片(只读,S-47 / Y-35)。运营端此前没有这条口(客户 / 飞手 / 监管
   * 各有读法),O9 整块「飞行节点凭证」吃它。照片是现签的短时效 URL(Y-02),
   * 不是公开直链 —— 「过期了」与「加载失败」在页面上是两句不同的话(见 OrderDetailView)。
   */
  getOrderProofs: (orderNo: string) =>
    request<{ items: Proof[] }>('GET', `/api/admin/orders/${encodeURIComponent(orderNo)}/proofs`),

  /** 代办推进(M-01):办理中 → 已办完 → 等接单。note 只进审计日志,客户看不到 */
  advanceAgency: (orderNo: string, to: OrderStatus, note?: string) =>
    request<AdminOrderDetail>('POST', `/api/admin/orders/${encodeURIComponent(orderNo)}/agency-transitions`, {
      ...(note ? { note } : {}),
      to,
    }),

  /** 人工指派(B8 的兜底)。佣金率快照在服务端写 —— 前端传不了,也不该传 */
  assignCarrier: (orderNo: string, carrierId: string, reason?: string) =>
    request<AdminOrderDetail>('POST', `/api/admin/orders/${encodeURIComponent(orderNo)}/assign`, {
      carrierId,
      ...(reason ? { reason } : {}),
    }),

  /**
   * 运营端代确认收货(S-55,Y-47)。收件人不注册 / 手机号填错时的兜底 ——
   * 签收一旦成为完成的闸门,就必须有人能在收件人不动的时候把闸门推开。
   * 签收方式记 ops 并写审计日志;幂等(已经签过时原样返回当前状态)。
   */
  confirmReceiptByOps: (orderNo: string) =>
    request<AdminOrderDetail>('POST', `/api/admin/orders/${encodeURIComponent(orderNo)}/receipt-confirmations`),

  getConfig: () => request<PlatformConfig>('GET', '/api/admin/config'),

  updateConfig: (input: PlatformConfig) => request<PlatformConfig>('PUT', '/api/admin/config', input),

  listAuditLogs: (q: { eventType?: AuditEventType; orderNo?: string; page?: number; pageSize?: number } = {}) => {
    const params = new URLSearchParams({ page: String(q.page ?? 1), pageSize: String(q.pageSize ?? 50) })
    if (q.eventType) params.set('eventType', q.eventType)
    if (q.orderNo) params.set('orderNo', q.orderNo)
    return request<Page<AuditLogItem>>('GET', `/api/admin/audit-logs?${params.toString()}`)
  },

  // ── S-11:监管端账号的开户与停用(M-07 的「由运营端开户与停用」)──

  listGovAccounts: () =>
    request<Page<GovAccount>>('GET', '/api/admin/gov-accounts?page=1&pageSize=100'),

  createGovAccount: (input: { username: string; password: string }) =>
    request<GovAccount>('POST', '/api/admin/gov-accounts', input),

  /**
   * 停用 / 启用。**只有 enabled 这一个开关** —— 口令不在 M-07 的「开户与停用」里,
   * 契约的 GovAccountPatch 也只有这一个字段。要改口令得先想清楚那是一个新能力。
   */
  patchGovAccount: (accountId: string, patch: { enabled: boolean }) =>
    request<GovAccount>('PATCH', `/api/admin/gov-accounts/${encodeURIComponent(accountId)}`, patch),

  // ── S-43:结算与对账(v2 · O2)─────────────────────────────
  //
  // 两条都是**只读**。这一页不发任何写请求 —— 打款走线下对公转账(paid_out 本期
  // 没有通道),取消退款走的是小程序那条链路(S-08),运营端在结算页上不制造状态。

  /** 结算流水列表。契约只有 page / pageSize —— 没有状态过滤,也没有聚合(S-43 的三张卡因此画破折号) */
  listSettlements: (q: { page?: number; pageSize?: number } = {}) => {
    const params = new URLSearchParams({ page: String(q.page ?? 1), pageSize: String(q.pageSize ?? 50) })
    return request<Page<Settlement>>('GET', `/api/admin/settlements?${params.toString()}`)
  },

  /** 单笔结算明细。五条钱的等式(probes/invariants.mjs)读的就是这个返回体 */
  getSettlement: (orderNo: string) =>
    request<Settlement>('GET', `/api/admin/settlements/${encodeURIComponent(orderNo)}`),
}
